import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth.js';
import { Dashboard } from './components/Dashboard/Dashboard.js';
import { Events } from './components/Events/Events.js';
import EventPhotos from './components/Photos/EventPhotos.js';
import EventParticipants from './components/Participants/EventParticipants.js';
import EventManage from './components/Events/EventManage.js';
import ScreenManagement from './components/Screens/ScreenManagement.js';
import { Login } from './components/Auth/Login.js';
import { Layout } from './components/Layout/Layout.js';
import './index.css';
import Reports from './components/Reports/Reports.js';

// Componente de ruta protegida
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  // Si no hay usuario, redirigir a login
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  
  // Si hay usuario, mostrar el contenido protegido
  return <>{children}</>;
};

// Componente de login con redirección automática
const LoginRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
   
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  // Si ya hay usuario autenticado, redirigir al dashboard
  if (user) {
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }
  
  // Si no hay usuario, mostrar el formulario de login
  return <Login />;
};

// Componente principal de la aplicación
const AppRoutes = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/screens"
          element={
            <ProtectedRoute>
              <Layout>
                <ScreenManagement />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/events/:id/photos"
          element={
            <ProtectedRoute>
              <Layout>
                <EventPhotos />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/events/:id/manage"
          element={
            <ProtectedRoute>
              <Layout>
                <EventManage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/events/:id/participants"
          element={
            <ProtectedRoute>
              <Layout>
                <EventParticipants />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/events"
          element={
            <ProtectedRoute>
              <Layout>
                <Events />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <Layout>
                <Reports />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <Layout>
                <div className="p-6">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">Facturación</h1>
                  <p className="text-gray-600">Página de facturación en desarrollo...</p>
                </div>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <div className="p-6">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">Configuración</h1>
                  <p className="text-gray-600">Página de configuración en desarrollo...</p>
                </div>
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Ruta por defecto - redirigir al dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

// Componente principal
const App = () => {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

export default App;
